import { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Block,
  BlockTitle,
  Button,
  Icon,
  Link,
  List,
  ListItem,
  PageContent,
  Sheet,
  Toolbar,
  f7,
} from "framework7-react";
import { selectCurrentGroup } from "../../redux/reducer/app-slice";
import { shouldOfferInstallHint } from "../../utils/install-platform";
import { isAssignmentPromptPending } from "../assign-me/assign-prompt";
import TransferCode from "../session-transfer/transfer-code";
import {
  closeInstallSheet,
  isInstallHintShown,
  markInstallHintShown,
  openInstallSheet,
  promptInstall,
  useInstall,
  wasStartedWithoutGroups,
} from "./install-state";

const HINT_DELAY = 800;

const useFirstGroupHint = (method) => {
  const group = useSelector(selectCurrentGroup);
  const due = shouldOfferInstallHint({
    method,
    startedWithoutGroups: wasStartedWithoutGroups(),
    hasParticipants: group?.participants?.length > 0,
    alreadyShown: isInstallHintShown(),
  });

  useEffect(() => {
    if (!due) return undefined;
    const show = () => {
      if (isInstallHintShown() || isAssignmentPromptPending()) return;
      if (document.querySelector(".popup.modal-in, .sheet-modal.modal-in"))
        return;
      markInstallHintShown();
      openInstallSheet();
    };
    const timer = setTimeout(show, HINT_DELAY);
    f7.on("popupClosed sheetClosed", show);
    return () => {
      clearTimeout(timer);
      f7.off("popupClosed sheetClosed", show);
    };
  }, [due]);
};

const IosSteps = ({ active }) => (
  <>
    <Block>
      Öffne Split It wie eine App – direkt vom Home-Bildschirm, ohne
      Browserleiste.
    </Block>
    <List className="install-steps" strong inset dividers>
      <ListItem title="Tippe in Safari auf „Teilen“">
        <Icon
          slot="media"
          ios="f7:square_arrow_up"
          md="material:ios_share"
          color="primary"
        />
      </ListItem>
      <ListItem title="Wähle „Zum Home-Bildschirm“">
        <Icon
          slot="media"
          ios="f7:plus_square"
          md="material:add_box"
          color="primary"
        />
      </ListItem>
    </List>
    <Block className="install-note">
      Siehst du „Teilen“ nicht, tippe zuerst auf „…“.
    </Block>
    <BlockTitle>Gruppen mitnehmen</BlockTitle>
    <Block>
      Die App startet leer. Tippe dort auf „Code eingeben“ – dann sind alle
      deine Gruppen da.
    </Block>
    <TransferCode active={active} />
  </>
);

const PromptStep = () => (
  <>
    <Block>
      Installiere Split It auf deinem Gerät – schnell erreichbar, ohne
      Browserleiste.
    </Block>
    <Block>
      <Button fill large onClick={promptInstall}>
        App installieren
      </Button>
    </Block>
  </>
);

const InstallSheet = () => {
  const { method, sheetOpened } = useInstall();
  useFirstGroupHint(method);
  const opened = sheetOpened && Boolean(method);

  return (
    <Sheet
      className="install-sheet"
      style={{ height: "auto" }}
      opened={opened}
      onSheetClosed={closeInstallSheet}
      swipeToClose
      backdrop
    >
      <Toolbar>
        <div className="left"></div>
        <div className="right">
          <Link sheetClose iconOnly aria-label="Schließen">
            <i className="icon icon-close" />
          </Link>
        </div>
      </Toolbar>
      <PageContent>
        <BlockTitle large>Split It als App</BlockTitle>
        {method === "ios" ? (
          <IosSteps active={opened} />
        ) : (
          <PromptStep />
        )}
      </PageContent>
    </Sheet>
  );
};

export default InstallSheet;
